/**
 * @file Component that implements the Login with Shop Default Authorize flow (with no side-effects).
 */
import {defineCustomElement} from '../../common/init';
import InputListener from '../../common/InputListener';
import {logError} from '../../common/logging';
import Bugsnag from '../../common/bugsnag';
import {IFrameEventSource} from '../../common/MessageEventSource';
import MessageListener from '../../common/MessageListener';
import {ShopSheetModal} from '../../common/shop-sheet-modal/shop-sheet-modal';
import {
  getAnalyticsTraceId,
  updateAttribute,
  isValidEmail,
  exchangeLoginCookie,
  debounce,
  ShopHubTopic,
} from '../../common/utils';
import {
  PAY_AUTH_DOMAIN,
  PAY_AUTH_DOMAIN_ALT,
  validateStorefrontOrigin,
} from '../../common/utils/urls';
import {I18n} from '../../common/translator/i18n';
import ConnectedWebComponent from '../../common/ConnectedWebComponent';
import {PayMessageSender} from '../../common/MessageSender';
import {
  AuthorizeModalTextActionType,
  LoginButtonMessageEventData,
  LoginButtonMessageEventData as MessageEventData,
  AuthorizeLoadedEvent,
  CustomFlowSideEffectEvent,
  OAuthParams,
  SdkErrorCode,
  ShopActionType,
  ShopUserMatchedEvent,
  VerificationStepChangedEvent,
  LoginButtonVersion as Version,
  AuthenticationLevel,
} from '../../types';
import {
  ATTRIBUTE_ANCHOR_SELECTOR,
  ATTRIBUTE_AUTO_OPEN,
  ATTRIBUTE_CLIENT_ID,
  ATTRIBUTE_DISABLE_SIGN_UP,
  ATTRIBUTE_EMAIL_VERIFICATION_REQUIRED,
  ATTRIBUTE_HIDE_BUTTON,
  ATTRIBUTE_REDIRECT_TYPE,
  ATTRIBUTE_REDIRECT_URI,
  ATTRIBUTE_STOREFRONT_ORIGIN,
  ATTRIBUTE_VERSION,
  ATTRIBUTE_ANALYTICS_CONTEXT,
  ATTRIBUTE_ANALYTICS_TRACE_ID,
  ATTRIBUTE_RESPONSE_TYPE,
  ATTRIBUTE_RESPONSE_MODE,
  ATTRIBUTE_CODE_CHALLENGE,
  ATTRIBUTE_CODE_CHALLENGE_METHOD,
  ATTRIBUTE_STATE,
  ATTRIBUTE_SCOPE,
  ATTRIBUTE_USE_PAY_ALT_DOMAIN,
  ATTRIBUTE_FLOW,
  ATTRIBUTE_KEEP_MODAL_OPEN,
  ATTRIBUTE_FLOW_VERSION,
  ERRORS,
  LOAD_TIMEOUT_MS,
  EMAIL_LISTENER_DEBOUNCE_MS,
  ATTRIBUTE_EMAIL,
  ATTRIBUTE_MODAL_TITLE,
  ATTRIBUTE_MODAL_DESCRIPTION,
  ATTRIBUTE_MODAL_LOGO_SRC,
  ATTRIBUTE_API_KEY,
  ATTRIBUTE_POP_UP_NAME,
  ATTRIBUTE_POP_UP_FEATURES,
  ATTRIBUTE_MODAL_BRAND,
  ATTRIBUTE_CONSENT_CHALLENGE,
  ATTRIBUTE_CHECKOUT_VERSION,
  ATTRIBUTE_SHOP_ID,
  ATTRIBUTE_FIRST_NAME,
  ATTRIBUTE_LAST_NAME,
  ATTRIBUTE_REQUIRE_VERIFICATION,
} from '../../constants/loginButton';
import {DefaultComponentAnalyticsContext} from '../../constants/loginDefault';

import {DefaultComponentMonorailTracker} from './analytics';
import {buildAuthorizeUrl} from './authorize';
import LoginDefaultView from './view/LoginDefaultView';
import {shouldHidePayCopy} from './utils/authorizeModalTexts';
import {validateModalCustomizations} from './utils/modalCustomizations';

export default class ShopLoginDefault extends ConnectedWebComponent {
  _rootElement: ShadowRoot;
  #analyticsTraceId = getAnalyticsTraceId();
  #clientId = '';
  #version: Version = '2';
  #storefrontOrigin = window.location.origin;
  #monorailTracker = new DefaultComponentMonorailTracker({
    elementName: 'shop-login-default',
    analyticsTraceId: this.#analyticsTraceId,
  });

  #redirectType: OAuthParams['redirectType'];
  #redirectUri: string | undefined;
  #popUpName: string | undefined;
  #popUpFeatures: string | undefined;

  #i18n: I18n | null = null;

  private _view: LoginDefaultView;
  private _emailVerificationRequired = false;
  private _disableSignUp = false;
  private _autoOpen = false;
  private _analyticsContext = DefaultComponentAnalyticsContext.Default;
  private _apiKey: string | undefined;

  private _usePayAltDomain = false;
  private _flow = ShopActionType.Default;
  private _flowVersion = 'unspecified';

  private _inputListener: InputListener | undefined;

  private _iframe: HTMLIFrameElement | undefined;
  private _iframeListener:
    | MessageListener<LoginButtonMessageEventData>
    | undefined;

  private _hideButton = false;
  private _anchorSelector = '';
  private _iframeMessenger?: PayMessageSender;
  private _iframeLoadTimeout: ReturnType<typeof setTimeout> | undefined;
  private _iframeLoaded = false;
  private _updateEmailAbortController: AbortController | undefined;
  private _debouncedUpdateUserInfo: (userInfo: {
    email: string;
    firstName?: string;
    lastName?: string;
  }) => void;

  private _responseType: string | undefined;
  private _responseMode: string | undefined;
  private _codeChallenge: string | undefined;
  private _codeChallengeMethod: string | undefined;
  private _state: string | undefined;
  private _scope: string | undefined;
  private _email = '';
  private _checkoutVersion: string | undefined;

  private _payLoaded: Promise<{userFound: boolean}> | undefined;

  private _authorizeModalOpened = false;
  private _keepModalOpen = false;
  private _consentChallenge: boolean | undefined;
  private _shopId: string | undefined;
  private _firstName: string | undefined;
  private _lastName: string | undefined;
  private _requireVerification = false;

  static get observedAttributes(): string[] {
    return [
      ATTRIBUTE_ANCHOR_SELECTOR,
      ATTRIBUTE_CLIENT_ID,
      ATTRIBUTE_VERSION,
      ATTRIBUTE_STOREFRONT_ORIGIN,
      ATTRIBUTE_EMAIL_VERIFICATION_REQUIRED,
      ATTRIBUTE_HIDE_BUTTON,
      ATTRIBUTE_DISABLE_SIGN_UP,
      ATTRIBUTE_AUTO_OPEN,
      ATTRIBUTE_REDIRECT_TYPE,
      ATTRIBUTE_REDIRECT_URI,
      ATTRIBUTE_ANALYTICS_CONTEXT,
      ATTRIBUTE_ANALYTICS_TRACE_ID,
      ATTRIBUTE_RESPONSE_TYPE,
      ATTRIBUTE_RESPONSE_MODE,
      ATTRIBUTE_CODE_CHALLENGE,
      ATTRIBUTE_CODE_CHALLENGE_METHOD,
      ATTRIBUTE_STATE,
      ATTRIBUTE_SCOPE,
      ATTRIBUTE_USE_PAY_ALT_DOMAIN,
      ATTRIBUTE_FLOW,
      ATTRIBUTE_FLOW_VERSION,
      ATTRIBUTE_EMAIL,
      ATTRIBUTE_MODAL_TITLE,
      ATTRIBUTE_MODAL_DESCRIPTION,
      ATTRIBUTE_MODAL_LOGO_SRC,
      ATTRIBUTE_API_KEY,
      ATTRIBUTE_POP_UP_NAME,
      ATTRIBUTE_POP_UP_FEATURES,
      ATTRIBUTE_MODAL_BRAND,
      ATTRIBUTE_CONSENT_CHALLENGE,
      ATTRIBUTE_CHECKOUT_VERSION,
      ATTRIBUTE_SHOP_ID,
      ATTRIBUTE_FIRST_NAME,
      ATTRIBUTE_LAST_NAME,
      ATTRIBUTE_REQUIRE_VERIFICATION,
    ];
  }

  constructor() {
    super();

    if (!customElements.get('shop-sheet-modal')) {
      customElements.define('shop-sheet-modal', ShopSheetModal);
    }

    this._rootElement = this.attachShadow({mode: 'open'});
    const onClose = () => {
      this.dispatchCustomEvent('modalclosed');
    };
    this._view = new LoginDefaultView(this._rootElement, onClose);

    this._debouncedUpdateUserInfo = debounce(({email, firstName, lastName}) => {
      this._updateUserInfo({
        email,
        firstName,
        lastName,
      });

      if (isValidEmail(email)) {
        this.#monorailTracker.trackEmailEnteredAction();
      }
    }, EMAIL_LISTENER_DEBOUNCE_MS);
  }

  #handleUserIdentityChange = () => {
    this._updateSrc(true);
  };

  attributeChangedCallback(
    name: string,
    _oldValue: string,
    newValue: string | null,
  ): void {
    // For boolean attributes only check if the attribute is passed, ignoring its value
    const attributePassed = Boolean(newValue !== null);

    switch (name) {
      case ATTRIBUTE_VERSION:
        this.#version = newValue as Version;
        this._updateSrc();
        break;
      case ATTRIBUTE_CLIENT_ID:
        this.#clientId = newValue || '';
        this._updateSrc();
        break;
      case ATTRIBUTE_RESPONSE_TYPE:
        this._responseType = newValue || '';
        this._updateSrc();
        break;
      case ATTRIBUTE_RESPONSE_MODE:
        this._responseMode = newValue || '';
        this._updateSrc();
        break;
      case ATTRIBUTE_CODE_CHALLENGE:
        this._codeChallenge = newValue || '';
        this._updateSrc();
        break;
      case ATTRIBUTE_CODE_CHALLENGE_METHOD:
        this._codeChallengeMethod = newValue || '';
        this._updateSrc();
        break;
      case ATTRIBUTE_STATE:
        this._state = newValue || '';
        this._updateSrc();
        break;
      case ATTRIBUTE_SCOPE:
        this._scope = newValue || '';
        this._updateSrc();
        break;
      case ATTRIBUTE_STOREFRONT_ORIGIN:
        this.#storefrontOrigin = newValue || window.location.origin;
        validateStorefrontOrigin(this.#storefrontOrigin);
        break;
      case ATTRIBUTE_EMAIL_VERIFICATION_REQUIRED:
        this._emailVerificationRequired = attributePassed;
        this._updateSrc();
        break;
      case ATTRIBUTE_HIDE_BUTTON:
        this._hideButton = attributePassed;
        this._view?.setContinueButtonVisible(attributePassed);
        this._updateSrc();
        break;
      case ATTRIBUTE_USE_PAY_ALT_DOMAIN:
        this._usePayAltDomain = attributePassed;
        this._updateSrc();
        break;
      case ATTRIBUTE_FLOW:
        this._flow = (newValue as ShopActionType) || ShopActionType.Default;
        this._view.setFlow(this._flow);
        this._updateSrc();
        break;
      case ATTRIBUTE_FLOW_VERSION:
        this._flowVersion = newValue || 'unspecified';
        this._view.setFlowVersion(this._flowVersion);
        this._updateMonorailTracker();
        this._updateSrc();
        break;
      case ATTRIBUTE_DISABLE_SIGN_UP:
        this._disableSignUp = attributePassed;
        this._updateSrc();
        break;
      case ATTRIBUTE_AUTO_OPEN:
        this._autoOpen = attributePassed;
        break;
      case ATTRIBUTE_REDIRECT_TYPE:
        this.#redirectType = newValue === 'pop_up' ? 'pop_up' : 'top_frame';
        this._updateSrc();
        break;
      case ATTRIBUTE_REDIRECT_URI:
        this.#redirectUri = newValue || undefined;
        this._updateSrc();
        break;
      case ATTRIBUTE_ANALYTICS_CONTEXT:
        this._analyticsContext =
          (newValue as DefaultComponentAnalyticsContext) ||
          DefaultComponentAnalyticsContext.Default;
        this._view.setAnalyticsContext(this._analyticsContext);
        this._updateMonorailTracker();
        this._updateSrc();
        break;
      case ATTRIBUTE_ANALYTICS_TRACE_ID:
        this.#analyticsTraceId = newValue || getAnalyticsTraceId();
        this._updateMonorailTracker();
        this._updateSrc();
        break;
      case ATTRIBUTE_EMAIL:
        this._initEmail(newValue || '');
        break;
      case ATTRIBUTE_ANCHOR_SELECTOR:
        this._anchorSelector = newValue || '';
        this._view?.setAnchorSelector(newValue || '');
        break;
      case ATTRIBUTE_MODAL_TITLE:
        this._view.setCustomizedModalContent({
          modalTitle: newValue || undefined,
        });
        this._updateSrc();
        break;
      case ATTRIBUTE_MODAL_DESCRIPTION:
        this._view.setCustomizedModalContent({
          modalDescription: newValue || undefined,
        });
        this._updateSrc();
        break;
      case ATTRIBUTE_MODAL_LOGO_SRC:
        this._view.setCustomizedModalContent({
          modalLogo: newValue || undefined,
        });
        this._updateSrc();
        break;
      case ATTRIBUTE_API_KEY:
        this._apiKey = newValue || undefined;
        this._updateSrc();
        break;
      case ATTRIBUTE_POP_UP_NAME:
        this.#popUpName = newValue || undefined;
        this._updateSrc();
        break;
      case ATTRIBUTE_POP_UP_FEATURES:
        this.#popUpFeatures = newValue || undefined;
        this._updateSrc();
        break;
      case ATTRIBUTE_MODAL_BRAND:
        this._view.setBrand(newValue || undefined);
        break;
      case ATTRIBUTE_CONSENT_CHALLENGE:
        this._consentChallenge = attributePassed;
        this._updateSrc();
        break;
      case ATTRIBUTE_CHECKOUT_VERSION:
        this._checkoutVersion = newValue || undefined;
        this._updateSrc();
        break;
      case ATTRIBUTE_SHOP_ID:
        this._shopId = newValue || undefined;
        this._updateSrc();
        break;
      case ATTRIBUTE_FIRST_NAME:
        this._firstName = newValue || undefined;
        break;
      case ATTRIBUTE_LAST_NAME:
        this._lastName = newValue || undefined;
        break;
      case ATTRIBUTE_REQUIRE_VERIFICATION:
        this._requireVerification = attributePassed;
        this._updateSrc();
        break;
    }
  }

  async connectedCallback(): Promise<void> {
    this.subscribeToHub(
      ShopHubTopic.UserStatusIdentity,
      this.#handleUserIdentityChange,
    );

    this._keepModalOpen = this.getBooleanAttribute(ATTRIBUTE_KEEP_MODAL_OPEN);
    this._hideButton = this.getBooleanAttribute(ATTRIBUTE_HIDE_BUTTON);
    this._view.setContinueButtonVisible(!this._hideButton);
    this._apiKey = this.getAttribute(ATTRIBUTE_API_KEY) || undefined;

    try {
      validateModalCustomizations(
        this._view.getCustomizedModalContent(),
        this._apiKey,
      );

      await this.#initTranslations();
      await this._initElements();
      validateStorefrontOrigin(this.#storefrontOrigin);
    } catch (error) {
      if (error instanceof Error) {
        logError(`Invalid config. ${error.message}`);
        this._handleError('invalid_config', SdkErrorCode.ApiUnavailable);
      }
    }
  }

  async #initTranslations() {
    try {
      // BUILD_LOCALE is used for generating localized bundles.
      // See ./scripts/i18n-dynamic-import-replacer-rollup.mjs for more info.
      // eslint-disable-next-line no-process-env
      const locale = process.env.BUILD_LOCALE || I18n.getDefaultLanguage();
      const dictionary = await import(`./translations/${locale}.json`);
      this.#i18n = new I18n({[locale]: dictionary});
      this._view.setTranslations(this.#i18n);
    } catch (error) {
      if (error instanceof Error) {
        Bugsnag.notify(error);
      }
    }
    return null;
  }

  async _initElements() {
    this._view.setModalAnalyticsTraceId(this.#analyticsTraceId);
    this._view.setMonorailTracker(this.#monorailTracker);
    this._view?.setAnchorSelector(this._anchorSelector);

    this._iframe = this._view.getIframe();
    this._updateSrc();

    const eventDestination: Window | undefined =
      this.ownerDocument?.defaultView || undefined;

    this._iframeListener = new MessageListener<LoginButtonMessageEventData>(
      new IFrameEventSource(this._iframe!),
      [PAY_AUTH_DOMAIN, PAY_AUTH_DOMAIN_ALT, this.#storefrontOrigin],
      this.#handlePostMessage.bind(this),
      eventDestination,
    );

    this._iframeMessenger = new PayMessageSender(this._iframe);
    this._payLoaded = this._iframeListener.waitForMessage('loaded');

    const {userFound} = await this._payLoaded;

    this._iframeLoaded = true;
    this.#clearLoadTimeout();

    if (userFound && this._autoOpen) {
      this._updateEmailAbortController?.abort();
      this._view.openAuthorizeModal();
    }
  }

  _initEmail(email: string) {
    this._email = email;
    if (isValidEmail(this._email)) {
      this._debouncedUpdateUserInfo({
        email: this._email,
        firstName: this._firstName,
        lastName: this._lastName,
      });
    } else {
      // // Send empty email to Pay to trigger a prop change if the same email is re-submitted
      this._debouncedUpdateUserInfo({
        email: '',
      });
    }
  }

  disconnectedCallback(): void {
    this.unsubscribeAllFromHub();
    this._iframeListener?.destroy();
    this.stopListeningToInput();
  }

  /**
   * Sends the given email to Pay if provided. Opens the modal regardless of whether the email
   * matched a user or not or if it was provided at all.
   *
   * @param {string} [email] Optional Email address to initialize the modal with.
   */
  async requestShow(email?: string): Promise<void> {
    if (!this._iframeLoaded) {
      Bugsnag.notify(new Error('requestShow called before iframe loaded'));
      await this._payLoaded;
    }

    if (this._authorizeModalOpened) {
      Bugsnag.notify(
        new Error('requestShow called when the modal is not closed'),
      );
      return;
    }

    if (email) {
      this._updateUserInfo({
        email,
        firstName: this._firstName,
        lastName: this._lastName,
      });
    }

    await this._payLoaded;
    this._view.openAuthorizeModal();
  }

  /**
   * Listens for changes to an email input field. If the input contains an email
   * that matches a Pay user, the modal is opened. If email-verification-required
   * is set, the modal will only open for email verified Pay users.
   *
   * Ensure disable-sign-up is set when calling this. Otherwise, if an email does
   * not match a Pay user the sign up form will open in the background but the modal
   * will not be shown.
   *
   * @param {object} inputElement Email input element to listen to.
   * @deprecated Use the email attribute instead
   */
  listenToInput(inputElement: HTMLInputElement): void {
    this.stopListeningToInput();

    const handleNewValue = debounce((value: string) => {
      if (isValidEmail(value)) {
        this._updateUserInfo({
          email: value,
        });
        this.#monorailTracker.trackEmailEnteredAction();
      } else {
        // Send empty email to Pay to trigger a prop change if the same email is re-submitted
        this._updateUserInfo({
          email: '',
        });
      }
    }, EMAIL_LISTENER_DEBOUNCE_MS);

    handleNewValue(inputElement.value);
    this._inputListener = new InputListener(inputElement, handleNewValue);
  }

  /**
   * Stops listening to input changes. Call `listenToInput` again to start listening again.
   */
  stopListeningToInput() {
    this._inputListener?.destroy();
  }

  async _updateUserInfo({
    email,
    firstName = '',
    lastName = '',
  }: {
    email: string;
    firstName?: string;
    lastName?: string;
  }): Promise<void> {
    /**
     * Ensures that we do not submit a new email to the iframe if the sheet modal is already visible to the user
     */
    if (this._authorizeModalOpened) {
      return;
    }

    if (
      this._updateEmailAbortController &&
      !this._updateEmailAbortController?.signal.aborted
    ) {
      this._updateEmailAbortController.abort();
    }

    this._updateEmailAbortController = new AbortController();

    try {
      const loaded = this._iframeLoaded;
      const {userFound} = await this._payLoaded!;

      if (!loaded && userFound && this._autoOpen) {
        return;
      }

      // Submit the name at the same time as the email, since an email change represents
      // a change in user and also causes the modal to appear.
      this._iframeMessenger?.postMessage({
        type: 'namesubmitted',
        firstName,
        lastName,
      });

      this._iframeMessenger?.postMessage({
        type: 'emailsubmitted',
        email,
        hideChange: email.length > 0,
      });

      const shopUserMatchedPromise = this._iframeListener!.waitForMessage(
        'shop_user_matched',
        this._updateEmailAbortController.signal,
      );
      const captchaChallengePromise = new Promise((resolve, reject) => {
        const waitForCaptcha = async () => {
          try {
            const {code} = await this._iframeListener!.waitForMessage(
              'error',
              this._updateEmailAbortController!.signal,
            );

            if (code === SdkErrorCode.CaptchaChallenge) {
              resolve(undefined);
            } else {
              waitForCaptcha();
            }
          } catch (err) {
            reject(err);
          }
        };

        waitForCaptcha();
      });

      await Promise.race([shopUserMatchedPromise, captchaChallengePromise]);

      this._view.openAuthorizeModal();

      // Ensures `waitForMessage` calls are cancelled after the modal is opened
      this._updateEmailAbortController.abort();
    } catch (err) {
      // no-op
    }
  }

  _updateSrc(forced?: boolean) {
    const iframe = this._view?.getIframe();
    if (iframe) {
      const oauthParams: OAuthParams = {
        clientId: this.#clientId,
        responseType: this._responseType,
        responseMode: this._responseMode,
        redirectType: this.#redirectType,
        redirectUri: this.#redirectUri,
        codeChallenge: this._codeChallenge,
        codeChallengeMethod: this._codeChallengeMethod,
        state: this._state,
        scope: this._scope,
      };
      const modalCustomized = this._view.isModalCustomized();
      const authorizeUrl = buildAuthorizeUrl({
        version: this.#version,
        analyticsTraceId: this.#analyticsTraceId,
        analyticsContext: this._analyticsContext,
        flow: this._flow,
        flowVersion: this._flowVersion,
        emailVerificationRequired: this._emailVerificationRequired,
        signUpEnabled: !this._disableSignUp,
        ...(this.#version === '1' && this.#clientId && {oauthParams}),
        usePayAltDomain: this._usePayAltDomain,
        hideCopy: shouldHidePayCopy(this._analyticsContext),
        ...(modalCustomized && {modalCustomized}),
        apiKey: this._apiKey,
        popupWindowParams: {
          popUpName: this.#popUpName,
          popUpFeatures: this.#popUpFeatures,
        },
        consentChallenge: this._consentChallenge,
        checkoutVersion: this._checkoutVersion,
        shopId: this._shopId,
        requireVerification: this._requireVerification,
      });

      if (!authorizeUrl) return;

      this.#initLoadTimeout();
      updateAttribute(iframe, 'src', authorizeUrl, forced);
      Bugsnag.leaveBreadcrumb('Iframe url updated', {authorizeUrl}, 'state');
    }
  }

  _updateMonorailTracker() {
    this.#monorailTracker = new DefaultComponentMonorailTracker({
      elementName: 'shop-login-default',
      analyticsTraceId: this.#analyticsTraceId,
      analyticsContext: this._analyticsContext,
      flowVersion: this._flowVersion,
    });
    this._view?.setMonorailTracker(this.#monorailTracker);
  }

  #initLoadTimeout() {
    this.#clearLoadTimeout();
    this._iframeLoadTimeout = setTimeout(() => {
      const error = ERRORS.temporarilyUnavailable;
      this.dispatchCustomEvent('error', {
        message: error.message,
        code: error.code,
      });
      // eslint-disable-next-line no-warning-comments
      // TODO: replace this bugsnag notify with a Observe-able event
      // Bugsnag.notify(
      //   new PayTimeoutError(`Pay failed to load within ${LOAD_TIMEOUT_MS}ms.`),
      //   {component: this.#component, src: this.iframe?.getAttribute('src')},
      // );
      this.#clearLoadTimeout();
    }, LOAD_TIMEOUT_MS);
  }

  #clearLoadTimeout() {
    if (!this._iframeLoadTimeout) return;
    clearTimeout(this._iframeLoadTimeout);
    this._iframeLoadTimeout = undefined;
  }

  async _handleCompleted(
    loggedIn: boolean,
    email?: string,
    customerAccessToken?: string,
    customerAccessTokenExpiresAt?: string,
    shouldFinalizeLogin?: boolean,
    shopPayInstallmentsOnboarded?: boolean,
  ) {
    if (loggedIn) {
      if (shouldFinalizeLogin) {
        await exchangeLoginCookie(this.#storefrontOrigin, Bugsnag.notify);
      }

      this.publishToHub(ShopHubTopic.UserStatusIdentity);
    }

    if (!this._keepModalOpen) {
      this._view.closeAuthorizeModal();
    }

    this._iframeListener?.destroy();
    this.stopListeningToInput();

    this.dispatchCustomEvent('completed', {
      loggedIn,
      email,
      customerAccessToken,
      customerAccessTokenExpiresAt,
      shopPayInstallmentsOnboarded,
    });

    this._maybeRedirect();
  }

  async _handleCustomFlowSideEffect(payload: CustomFlowSideEffectEvent) {
    // currently only used for Prequal flow, but can be extended to other flows
    switch (payload.flow) {
      case ShopActionType.Prequal:
        this.dispatchCustomEvent('prequal_flow_side_effect', {
          shopPayInstallmentsOnboarded: payload.shopPayInstallmentsOnboarded,
        });
        break;
    }
  }

  _maybeRedirect(): void {
    if (this.#redirectType === 'pop_up') return;
    if (!this.#redirectUri) return;

    try {
      let redirectUri = this.#redirectUri;
      if (this.#analyticsTraceId) {
        const queryParams = {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          analytics_trace_id: this.#analyticsTraceId,
        };
        const searchParams = new URLSearchParams(queryParams);
        redirectUri = redirectUri.concat(`?${searchParams.toString()}`);
      } else {
        Bugsnag.notify(
          new Error(
            'Missing analytics trace ID when redirecting to account page',
          ),
        );
      }

      window.location.assign(redirectUri);
    } catch (error) {
      if (error instanceof Error) {
        Bugsnag.notify(error);
      }
    }
  }

  _handleError(message: string, code: SdkErrorCode): void {
    this.dispatchCustomEvent('error', {
      message,
      code,
    });
    Bugsnag.notify(new Error(`Authorize Error: ${message} (${code}).`));
    this.#clearLoadTimeout();
  }

  _onPopUpOpened(payload: {
    didOpen?: boolean;
    popUpName?: string;
    popUpFeatures?: string;
  }): void {
    if (payload.didOpen) {
      this._view.dispatch({
        type: AuthorizeModalTextActionType.PopUpOpened,
        payload,
      });
    }

    this.dispatchCustomEvent('popuploading', payload);
  }

  _onLoaded(payload: AuthorizeLoadedEvent) {
    this._view.setAuthenticationLevel(
      payload?.authenticationLevelRequired || AuthenticationLevel.Phone,
    );
    this._view.dispatch({
      type: AuthorizeModalTextActionType.Init,
      payload,
    });
  }

  _onUserMatched({
    hasName = false,
    userCookieExists = false,
    personalizeConsentChallenge = false,
  }: ShopUserMatchedEvent): void {
    this._view.dispatch({
      type: AuthorizeModalTextActionType.UserMatched,
      payload: {
        hasName,
        userCookieExists,
        personalizeConsentChallenge,
      },
    });
    this.dispatchCustomEvent('shopusermatched');
  }

  _onUserNotMatched(): void {
    this._view.dispatch({
      type: AuthorizeModalTextActionType.UserNotMatched,
    });
    this.dispatchCustomEvent('shopusernotmatched');
  }

  _onEmailChangeRequested(): void {
    this._view.dispatch({
      type: AuthorizeModalTextActionType.Restart,
    });
    this.dispatchCustomEvent('restarted');
  }

  _onVerificationStepChanged(payload: VerificationStepChangedEvent): void {
    this._view.dispatch({
      type: AuthorizeModalTextActionType.VerificationStepChanged,
      payload,
    });
  }

  #handlePostMessage(data: MessageEventData) {
    switch (data.type) {
      case 'loaded':
        this._onLoaded(data);
        break;
      case 'resize_iframe':
        this._view.resizeIframe(data.height, data.width);
        break;
      case 'completed':
        this._handleCompleted(
          data.loggedIn,
          data.email,
          data.customerAccessToken,
          data.customerAccessTokenExpiresAt,
          data.shouldFinalizeLogin,
        );
        break;
      case 'error':
        this._handleError(data.message, data.code);
        break;
      case 'close_requested':
        this._view.closeAuthorizeModal();
        break;
      case 'shop_user_matched':
        this._onUserMatched(data);
        break;
      case 'pop_up_opened':
        this._onPopUpOpened(data);
        break;
      case 'shop_user_not_matched':
        this._onUserNotMatched();
        break;
      case 'email_change_requested':
        this._onEmailChangeRequested();
        break;
      case 'verification_step_changed':
        this._onVerificationStepChanged(data);
        break;
      case 'custom_flow_side_effect':
        this._handleCustomFlowSideEffect(data);
        break;
    }
  }
}

/**
 * Define the shop-login-default custom element.
 */
export function defineElement() {
  defineCustomElement('shop-login-default', ShopLoginDefault);
}
